import { useTheme } from "@emotion/react";
import { Arrange, Typography } from "@fullscript/aviary-web";
import { useTranslation, Trans } from "react-i18next";

import {
  SvgHipaaCompliantGrey,
  SvgHipaaLogoPrimary,
  SvgSecureCheckoutPrimary,
  SvgHipaaLogoWhite,
  SvgSecureCheckoutWhite,
} from "@shared/assets/logo";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";

import { getSubtleFootnoteProps } from "../getSubtleFootnoteProps";
import { inverseSubtleText } from "../PageFooter.styles";
import type { PageFooterProps } from "../PageFooter.types";

import * as styles from "./FooterIcons.styles";

const FooterIcons = ({ type, hideHipaaLogo, isInverseTheme = false }: PageFooterProps) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const currentTheme = useTheme();

  const isDS4DarkMode = currentTheme.theme === "dark";

  const renderInverseHipaaLogo = (ariaLabel: string) => (
    <Arrange
      isWrapped={false}
      alignment="center"
      gap="xsmall"
      role="img"
      aria-label={ariaLabel}
      customCss={styles.inverseHipaaBadge}
    >
      <SvgHipaaCompliantGrey css={styles.hipaaGreyIcon} aria-hidden />
      <Arrange isVertical gap="none" customCss={styles.hipaaTextStack}>
        <Typography
          variant="caption"
          fontWeight="bold"
          intention="inverse"
          isMarginless
          aria-hidden
        >
          {t(l.pageFooter.hipaaBadgeWordmarkTop)}
        </Typography>
        <Typography
          variant="smallest"
          fontWeight="bold"
          intention="inverse"
          isMarginless
          aria-hidden
        >
          {t(l.pageFooter.hipaaBadgeWordmarkBottom)}
        </Typography>
      </Arrange>
    </Arrange>
  );

  const renderHipaaLogo = (ariaLabel: string) => {
    if (!isUS) return null;

    if (isInverseTheme) {
      return renderInverseHipaaLogo(ariaLabel);
    }

    if (isDS4DarkMode) return <SvgHipaaLogoWhite role="img" aria-label={ariaLabel} />;

    return <SvgHipaaLogoPrimary role="img" aria-label={ariaLabel} />;
  };

  const renderProductIcons = () => {
    if ((type === "product" || type === "labs") && !hideHipaaLogo) {
      return <div>{renderHipaaLogo(t(l.aviary.footer.HIPAAalttext))}</div>;
    }
  };

  const renderSecureCheckoutIcon = (ariaLabel: string) => {
    if (isDS4DarkMode) return <SvgSecureCheckoutWhite role="img" aria-label={ariaLabel} />;

    return <SvgSecureCheckoutPrimary role="img" aria-label={ariaLabel} />;
  };

  const renderCheckoutIcons = () => {
    if (type === "checkout") {
      return (
        <Arrange isWrapped={false} alignment="center">
          {renderSecureCheckoutIcon(t(l.aviary.footer.SecureCheckoutIcon))}
          <Typography
            {...getSubtleFootnoteProps(isInverseTheme)}
            customCss={isInverseTheme ? [inverseSubtleText, styles.secureText] : styles.secureText}
          >
            <Trans i18nKey={l.aviary.footer.SecureCheckout}>
              Fullscript ensures all payment information is <strong>stored securely</strong>.
            </Trans>
          </Typography>
        </Arrange>
      );
    }
  };

  return (
    <Arrange alignment="center" justify="center">
      {renderProductIcons()}
      {renderCheckoutIcons()}
    </Arrange>
  );
};

export { FooterIcons };
